import PermissionButton from '../../permissions/PermissionButton';
import React, { useState, useMemo } from 'react';
import { Form, Input, InputNumber, Button, Row, Col, Card, Upload, Checkbox, message, Divider, Space, Typography } from 'antd';
import { useCreateArticle } from '../../hooks/articleHooks';
import { FamilleSelect, SousFamilleSelect, FournisseurSelect } from './ArticleRelationSelects';
import axios from 'axios';
import {
  IdcardOutlined,
  TagsOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  UploadOutlined,
  DollarCircleOutlined,
  PercentageOutlined,
  TagOutlined,
  StockOutlined,
  BoxPlotOutlined,
  SafetyCertificateOutlined,
  FundProjectionScreenOutlined,
  UserOutlined,
  ClusterOutlined
} from "@ant-design/icons";

const { Paragraph } = Typography;


const ArticleForm = ({ onSuccess, initialValues = {} }) => {
  const { handleCreate } = useCreateArticle();
  const [form] = Form.useForm();
  const familyCode = Form.useWatch("code_fam", form);
  const instance = axios.create();
  const [submitting, setSubmitting] = useState(false);
  const mergedInitialValues = useMemo(() => ({
    TVA: 19,
    VENTE_BLOQ: false,
    ACHAT_BLOQ: false,
    TRANS_BLOQ: false,
    LIQUIDER: false,
    ...initialValues
  }), [initialValues]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const imageUrl = await handleUpload();

      const payload = {
        ...values,
        ARTICLE: values.ARTICLE || values.nom || values.articleName || values.ARTICLE_LIB || '',
        photo: imageUrl || values.photo || null,
        UAF: values.UAF ? parseInt(values.UAF, 10) : null,
        code_fam: values.code_fam || null,
        code_sfam: values.code_sfam || null,
        STK_MAX: values.STK_MAX ? parseInt(values.STK_MAX, 10) : null,
        STK_SEC: values.STK_SEC ? parseInt(values.STK_SEC, 10) : null,
        UVC: values.UVC ? parseInt(values.UVC, 10) : null,
        STK_MIN: values.STK_MIN ? parseInt(values.STK_MIN, 10) : null,
        qte_stk: values.qte_stk ? parseInt(values.qte_stk, 10) : null,
        PA_HT: values.PA_HT ? parseFloat(values.PA_HT) : null,
        PV_HT: values.PV_HT ? parseFloat(values.PV_HT) : null,
        TVA: values.TVA ? parseFloat(values.TVA) : null,
        PA_TTC: values.PA_TTC ? parseFloat(values.PA_TTC) : null,
        PV_TTC: values.PV_TTC ? parseFloat(values.PV_TTC) : null,
        REMISEMAX: values.REMISEMAX ? parseFloat(values.REMISEMAX) : null,
      };

      await handleCreate(payload);
      message.success("Article créé avec succès");
      form.resetFields();
      setFileList([]);
      onSuccess?.();
    } catch (error) {
      console.error('Error while creating article', error);
      message.error(error?.response?.data?.message || error?.message || "La création de l'article a échoué");
    } finally {
      setSubmitting(false);
    }
  };
  

  // Upload properties
  

  const [fileList, setFileList] = useState([]);

  const handleFileChange = info => {
    setFileList(info.fileList);
  };


  const handleUpload = async () => {
    if (fileList.length > 0 && form.getFieldValue('code_art')) {
      const formData = new FormData();
      formData.append("file", fileList[0].originFileObj);
      const articleCode = form.getFieldValue('code_art');

      formData.append("public_id", articleCode);
      formData.append("original_filename", articleCode);
      formData.append("upload_preset", "uozkyhrp");
      formData.append("cloud_name", "dggqqwrib");

      try {
        const response = await instance.post("https://api.cloudinary.com/v1_1/dggqqwrib/image/upload", formData);
        const data = response.data;
        return data.secure_url;
      } catch (error) {
        console.error('Error uploading file:', error);
        message.error("Impossible de téléverser l'image. Veuillez réessayer.");
        return '';
      }
    }
    return '';
  };
  

  const handleValueChange = (_, allValues = {}) => {
    const paHt = parseFloat(allValues.PA_HT);
    const pvHt = parseFloat(allValues.PV_HT);
    const tvaValue = parseFloat(allValues.TVA);
    const taux = Number.isNaN(tvaValue) ? null : tvaValue / 100;

    const newFields = {};
    if (!Number.isNaN(paHt) && taux !== null) {
      newFields.PA_TTC = Number((paHt * (1 + taux)).toFixed(3));
    }
    if (!Number.isNaN(pvHt) && taux !== null) {
      newFields.PV_TTC = Number((pvHt * (1 + taux)).toFixed(3));
    }

    if (Object.keys(newFields).length > 0) {
      form.setFieldsValue(newFields);
    }
  };

  // const titleStyle = {
  //   fontSize: "24px", // Increased font size for titles
  //   fontWeight: "bold",
  //   color: "#333", // Darker font color for better visibility
  //   marginBottom: "16px",
  //   borderBottom: "2px solid #ccc", // Separator line
  //   paddingBottom: "10px", // Spacing between title and separator line
  // };

  const cardStyle = {
    marginTop: "20px",
    backgroundColor: "#f8f8f8", // Lighter than the main background for emphasis
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", // Optional: adds subtle shadow for depth
  };

  const cardContentStyle = {
    paddingLeft: "24px", // Add left padding to card content
    paddingTop: "10px",
  };

  const titleStyle = {
    fontSize: "18px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "16px",
  };


  return (
    <Card
      title="Créer un article"
      bordered={false}
      style={{
        width: '100%',
        ...cardStyle,
      }}
    >
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Renseignez les informations essentielles puis validez : les montants TTC sont calculés automatiquement
        à partir du HT et du taux de TVA.
      </Paragraph>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleValueChange}
        initialValues={mergedInitialValues}
        scrollToFirstError
      >
        <Row gutter={16} style={cardContentStyle}>
          <Col span={24}>
            <Divider orientation="left">Informations générales</Divider>
          </Col>
          <Col span={12}>
            <Form.Item label={<span><IdcardOutlined /> Code article</span>} name="code_art" rules={[{ required: true, message: 'Le code article est requis' }]}>
              <Input placeholder="Saisir le code article" autoComplete="off" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={<span><FileTextOutlined /> Nom</span>} name="nom" rules={[{ required: true, message: 'Le nom de l’article est requis' }]}>
              <Input placeholder="Libellé commercial" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={<span><TagsOutlined /> Référence OEM</span>} name="REF_OEM">
              <Input placeholder="Optionnel" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={<span><UploadOutlined /> Photo</span>}>
              <Upload
                beforeUpload={() => false}
                listType="picture"
                onChange={handleFileChange}
                fileList={fileList}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Choisir un visuel</Button>
              </Upload>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label={<span><InfoCircleOutlined /> Description</span>} name="desc">
              <Input.TextArea rows={3} placeholder="Ajoutez une description courte (facultatif)" showCount maxLength={250} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Divider orientation="left">Fournisseur & famille</Divider>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<span><UserOutlined /> Fournisseur</span>}
              name="code_frs"
              rules={[{ required: true, message: 'Sélectionnez un fournisseur' }]}
            >
              <FournisseurSelect />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<span><ClusterOutlined /> Famille</span>}
              name="code_fam"
            >
              <FamilleSelect onChange={() => form.setFieldsValue({ code_sfam: null })} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Sous-famille" name="code_sfam">
              <SousFamilleSelect familyCode={familyCode} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Divider orientation="left">Prix & taxes</Divider>
          </Col>
          <Col span={6}>
            <Form.Item label={<span><TagsOutlined /> Unité d’achat</span>} name="UAF">
              <Input placeholder="Indiquer l’unité d’achat" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={<span><DollarCircleOutlined /> Prix achat HT</span>} name="PA_HT" rules={[{ required: true, message: 'Prix achat requis' }]} extra="Utilisé pour calculer le coût de revient.">
              <InputNumber min={0} step={0.001} placeholder="Prix d’achat HT" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={<span><DollarCircleOutlined /> Prix vente HT</span>} name="PV_HT" rules={[{ required: true, message: 'Prix vente requis' }]} extra="Prix public hors taxe.">
              <InputNumber min={0} step={0.001} placeholder="Prix de vente HT" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={<span><PercentageOutlined /> TVA (%)</span>} name="TVA" rules={[{ required: true, message: 'TVA requise' }]} extra="Saisir un taux en pourcentage.">
              <InputNumber min={0} step={0.1} placeholder="Taux de TVA" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={<span><TagOutlined /> PA TTC</span>} name="PA_TTC">
              <Input readOnly />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={<span><TagOutlined /> PV TTC</span>} name="PV_TTC">
              <Input readOnly />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={<span><PercentageOutlined /> Remise max %</span>} name="REMISEMAX" extra="Limite la remise appliquée lors de la vente.">
              <InputNumber min={0} max={100} step={0.1} placeholder="Pourcentage autorisé" style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Divider orientation="left">Stock & logistique</Divider>
          </Col>
          <Col span={6}>
            <Form.Item label={<span><StockOutlined /> Stock max</span>} name="STK_MAX">
              <InputNumber min={0} placeholder="Quantité maximale" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={<span><StockOutlined /> Stock min</span>} name="STK_MIN">
              <InputNumber min={0} placeholder="Quantité minimale" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={<span><SafetyCertificateOutlined /> Stock de sécurité</span>} name="STK_SEC">
              <InputNumber min={0} placeholder="Stock cible" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={<span><BoxPlotOutlined /> Stock initial</span>} name="qte_stk">
              <InputNumber placeholder="Quantité initiale" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={<span><FundProjectionScreenOutlined /> UVC</span>} name="UVC">
              <InputNumber min={0} placeholder="Unité conditionnée" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={18}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Form.Item name="VENTE_BLOQ" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>Vente bloquée</Checkbox>
              </Form.Item>
              <Form.Item name="ACHAT_BLOQ" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>Achat bloqué</Checkbox>
              </Form.Item>
              <Form.Item name="TRANS_BLOQ" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>Transfert bloqué</Checkbox>
              </Form.Item>
              <Form.Item name="LIQUIDER" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>Liquidation</Checkbox>
              </Form.Item>
            </Space>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 16 }}>
              <Button onClick={() => form.resetFields()} disabled={submitting}>
                Réinitialiser
              </Button>
              <PermissionButton permissionModule="articles" permissionAction="create" type="primary" htmlType="submit" loading={submitting}>
                Créer l’article
              </PermissionButton>
            </Space>
          </Col>
        </Row>
      </Form>
    </Card>
  );

};

export default ArticleForm;
